import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, CheckCircle2, AlertTriangle, Loader2, FlaskConical, ShieldCheck, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { V } from "@/lib/vocab";

const Tile = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-sand-200 bg-white px-3 py-2.5">
    <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-ink/45">{label}</div>
    <div className="mt-0.5 text-lg font-extrabold text-ink leading-tight">{value}</div>
    {sub && <div className="text-[11px] text-ink/50">{sub}</div>}
  </div>
);

const min = (v) => (v == null ? "—" : `${v} min`);
const FREQ = { off: "Off", monthly: "Monthly", quarterly: "Quarterly" };

// Step 5 — the numbers, the ladder's health and the drill, in one strip above the ladder.
export function EmergencyReadiness({ tenantId, canEdit, autoOpenDrill, onDrillConsumed, onChanged, onOpenRecord, onRemind, refreshKey }) {
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get(`/tenants/${tenantId}/emergency/readiness`).then((r) => setData(r.data)).catch(() => {}), [tenantId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (autoOpenDrill && canEdit) { setConfirm(true); onDrillConsumed?.(); } }, [autoOpenDrill, canEdit, onDrillConsumed]);

  const runDrill = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/emergency/drill`);
      setConfirm(false);
      if (!r.data.created) toast.message("A drill is already running — opening it");
      else toast.success(`Drill sent to ${r.data.alerted} responder${r.data.alerted === 1 ? "" : "s"} — waiting for the first ACK`);
      await load(); onChanged?.(); onOpenRecord?.(r.data.event.id);
    } catch (e) { toast.error(errMsg(e, "Couldn't start the drill")); }
    finally { setRunning(false); }
  };

  if (!data) return null;
  const s = data.stats, h = data.health, d = data.drill;
  const unverifiedIds = (h.unverified || []).map((u) => u.user_id);
  return (
    <CollapsibleCard id="emergency.readiness" icon={Activity} title="Readiness" testid="emergency-readiness"
      summary={`last ${s.days} days · ${s.incidents} emergenc${s.incidents === 1 ? "y" : "ies"} · ladder ${h.ok ? "healthy" : "needs attention"}${d.due ? " · drill due" : ""}`}>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Tile testid="readiness-incidents" label="Emergencies" value={s.incidents} sub={`${s.resolved} resolved${s.open ? ` · ${s.open} open` : ""}`} />
        <Tile testid="readiness-ack" label="Median to ACK" value={min(s.median_minutes_to_ack)} sub={s.never_acked ? `${s.never_acked} never acknowledged` : "first responder to take it"} />
        <Tile testid="readiness-resolve" label="Median to resolve" value={min(s.median_minutes_to_resolve)} sub="reported → resolved" />
        <Tile testid="readiness-photos" label="With photos" value={`${s.photo_pct}%`} sub={`${s.with_photos} of ${s.incidents}`} />
        <Tile testid="readiness-drills" label="Drills" value={s.drills} sub={s.drills ? `${s.drills_acked} acknowledged · median ${min(s.drill_median_minutes_to_ack)}` : "none this window"} />
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div data-testid="ladder-health" className={`rounded-xl border p-4 ${h.ok ? "border-emerald-200 bg-emerald-50" : h.warnings.some((w) => w.severity === "high") ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}`}>
          <div className="flex items-center gap-2 text-sm font-bold text-ink">
            {h.ok ? <ShieldCheck className="h-4 w-4 text-emerald-700" /> : <AlertTriangle className="h-4 w-4 text-amber-700" />} Ladder health
          </div>
          <p className="mt-1 text-xs text-ink/60">Tier 1: {h.tier1_total} on the ladder · {h.tier1_callable} reachable by call · {h.tier1_textable} by text</p>
          {h.ok ? (
            <p className="mt-2 text-sm text-emerald-900" data-testid="ladder-health-ok">Everyone on tier 1 can be called tonight.</p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {h.warnings.map((w, i) => (
                <li key={i} data-testid={`ladder-warning-${w.code}`} className="text-sm text-ink">{w.text}</li>
              ))}
            </ul>
          )}
          {canEdit && unverifiedIds.length > 0 && onRemind && (
            <button data-testid="ladder-health-nudge" onClick={() => onRemind(unverifiedIds)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100">
              Send verify nudge to {unverifiedIds.length}
            </button>
          )}
        </div>

        <div data-testid="drill-card" className="rounded-xl border border-sand-200 bg-sand-50 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-bold text-ink"><FlaskConical className="h-4 w-4 text-ink/60" /> Drill</div>
            <span data-testid="drill-frequency" className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink/60">{FREQ[d.frequency] || d.frequency}</span>
          </div>
          <p className="mt-1 text-xs text-ink/60" data-testid="drill-last">
            {d.last_drill_at
              ? <>Last drill {d.last_drill_when}{d.last_drill?.owner ? ` — ${d.last_drill.owner} acknowledged${d.last_drill.minutes_to_ack != null ? ` in ${d.last_drill.minutes_to_ack} min` : ""}` : d.last_drill?.status === "expired" ? " — nobody acknowledged" : ""}.</>
              : "No drill has been run yet."}
            {d.due && <span data-testid="drill-due" className="ml-1 font-bold text-amber-800">Due now.</span>}
            {!d.due && d.next_due_on && <span className="ml-1">Next due {d.next_due_on}.</span>}
          </p>
          <p className="mt-2 text-xs text-ink/55">Texts and emails tier 1 exactly like a real alert (marked DRILL), times the first ACK, then closes itself. No calls, no {V.lc("person")}, no ticket.</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {d.open_drill_id ? (
              <button data-testid="drill-open-record" onClick={() => onOpenRecord?.(d.open_drill_id)}
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-amber-700">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Drill in progress — waiting for ACK <ArrowRight className="h-3 w-3" />
              </button>
            ) : canEdit && (
              <button data-testid="drill-run-btn" onClick={() => setConfirm(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-bold text-white hover:bg-ink-700">
                <FlaskConical className="h-3.5 w-3.5" /> Run a drill
              </button>
            )}
            {d.last_drill && !d.open_drill_id && (
              <button data-testid="drill-last-record" onClick={() => onOpenRecord?.(d.last_drill.id)} className="text-xs font-semibold text-ink/60 hover:text-ink">Last drill record →</button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={confirm} onOpenChange={(o) => !o && setConfirm(false)}>
        <DialogContent className="max-w-md" data-testid="drill-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><FlaskConical className="h-4 w-4" /> Run an emergency drill?</DialogTitle>
            <DialogDescription>
              Tier 1 ({h.tier1_textable} by text{h.tier1_total > h.tier1_textable ? `, ${h.tier1_total - h.tier1_textable} without a mobile` : ""}) gets a text and an email marked <b>DRILL</b>. The first ACK closes it and the managers get the result. Nobody is called and no {V.lc("person")} is involved.
            </DialogDescription>
          </DialogHeader>
          {h.tier1_textable === 0 && (
            <p data-testid="drill-nobody" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">Nobody on tier 1 can be texted — add responders with a mobile first.</p>
          )}
          <DialogFooter>
            <button data-testid="drill-cancel" onClick={() => setConfirm(false)} className="rounded-full border border-sand-300 px-4 py-2 text-sm font-semibold text-ink">Cancel</button>
            <button data-testid="drill-confirm" onClick={runDrill} disabled={running}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-50">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Send the drill
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CollapsibleCard>
  );
}
